import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";

const abs = (url) => (!url ? url : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);

export function overlayBackground({ style = "gradient", color = "#000000", opacity = 40 } = {}) {
  if (style === "none") return "transparent";
  const a = Math.max(0, Math.min(1, (Number(opacity) || 0) / 100));
  const hex = (color || "#000000").replace("#", "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex.slice(0, 6).padEnd(6, "0");
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  const rgba = (mult) => `rgba(${r},${g},${b},${Math.min(1, a * mult)})`;
  if (style === "solid") return rgba(1);
  if (style === "vignette") return `radial-gradient(ellipse at center, ${rgba(0.15)} 0%, ${rgba(1)} 100%)`;
  return `linear-gradient(to bottom, ${rgba(1)} 0%, ${rgba(0.5)} 45%, ${rgba(1.5)} 100%)`;
}

function HeroSkeleton() {
  return (
    <section className="relative h-screen w-full overflow-hidden bg-ink" data-testid="hero-slideshow-loading" aria-label="Loading hero">
      <div className="absolute inset-0 bg-ink animate-pulse" />
      <div className="absolute inset-x-0 bottom-24 md:bottom-32 container-lux">
        <div className="h-3 w-28 bg-ivory/10 animate-pulse" />
        <div className="mt-5 h-24 md:h-36 max-w-3xl bg-ivory/10 animate-pulse" />
        <div className="mt-6 h-4 w-64 bg-ivory/10 animate-pulse" />
      </div>
    </section>
  );
}

export default function Hero() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/hero-slides").then(r => {
      if (cancelled) return;
      setSlides(Array.isArray(r.data) ? r.data.map(s => ({ ...s, image: abs(s.image) })).filter(s => s.image) : []);
    }).catch(() => {
      if (!cancelled) setSlides([]);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <HeroSkeleton />;
  if (!slides.length) {
    return <section className="relative h-screen w-full overflow-hidden bg-ink" data-testid="hero-empty" />;
  }

  return (
    <section className="relative h-screen w-full overflow-hidden bg-ink" data-testid="hero-slideshow">
      <Swiper modules={[Autoplay, EffectFade, Pagination]} effect="fade" fadeEffect={{ crossFade: true }} speed={1400} loop={slides.length > 1} autoplay={{ delay: 6500, disableOnInteraction: false }} pagination={{ clickable: true }} className="h-full w-full">
        {slides.map((s, i) => (
          <SwiperSlide key={s.id || i}>
            <div className="relative h-full w-full">
              <img src={s.image} alt={`${s.title_line1 || ""} ${s.title_line2 || ""}`} className="hero-ken absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: overlayBackground({ style: s.overlay_style, color: s.overlay_color, opacity: s.overlay_opacity }) }} />
              <div className="relative z-10 h-full container-lux flex flex-col justify-end pb-24 md:pb-32 text-ivory">
                {s.kicker && <p className="overline text-ivory/80 animate-fade-up" style={{ animationDelay: "150ms" }}>{s.kicker}</p>}
                <h1 className="mt-4 font-serif tracking-tighter font-light text-6xl md:text-8xl lg:text-9xl leading-[0.92] animate-fade-up" style={{ animationDelay: "350ms" }}>
                  {s.title_line1}{s.title_line2 && <><br/><em className="not-italic">{s.title_line2}</em></>}
                </h1>
                {s.subtitle && <p className="mt-6 text-sm md:text-base text-ivory/70 max-w-md animate-fade-up" style={{ animationDelay: "600ms" }}>{s.subtitle}</p>}
                {s.cta_text && <div className="mt-10 animate-fade-up" style={{ animationDelay: "800ms" }}><Link to={s.cta_link || "/collections"} className="lux-btn-light" data-testid={`hero-cta-${i}`}>{s.cta_text}</Link></div>}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="absolute bottom-8 left-0 right-0 z-20 container-lux flex items-center justify-between text-ivory/70 text-[11px] tracking-[0.28em] uppercase pointer-events-none">
        <span>Est. New Delhi</span><span>Scroll ↓</span>
      </div>
    </section>
  );
}
